import { type CSSProperties, type MouseEvent, useEffect, useState } from "react";
import { Card } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { useIsMobile } from "../lib/hooks";
import type { ScheduleEvent, Student } from "../lib/types";

// ── Time-grid constants ───────────────────────────────────────────────────
const PX_PER_HOUR = 64;
const START_HOUR = 8;
const END_HOUR = 21;
const TOTAL_H = (END_HOUR - START_HOUR) * PX_PER_HOUR; // 832px

function timeToY(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return ((h - START_HOUR) + m / 60) * PX_PER_HOUR;
}

function timeToHeight(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return Math.max(((eh * 60 + em) - (sh * 60 + sm)) / 60 * PX_PER_HOUR, 24);
}

function yToTime(y: number): string {
  const totalMin = Math.round((y / PX_PER_HOUR * 60) / 15) * 15;
  const clampedMin = Math.max(0, Math.min((END_HOUR - START_HOUR - 1) * 60, totalMin));
  const h = START_HOUR + Math.floor(clampedMin / 60);
  const m = clampedMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function dateToDowStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Overlap layout ────────────────────────────────────────────────────────
interface EventLayout {
  event: ScheduleEvent;
  leftFrac: number;
  widthFrac: number;
}

function layoutDayEvents(evs: ScheduleEvent[]): EventLayout[] {
  if (evs.length === 0) return [];
  const sorted = [...evs].sort((a, b) =>
    a.slot.localeCompare(b.slot) || b.end_time.localeCompare(a.end_time)
  );
  const colEnd: string[] = [];
  const assigned: number[] = [];
  for (const ev of sorted) {
    let col = colEnd.findIndex((end) => end <= ev.slot);
    if (col === -1) { col = colEnd.length; colEnd.push(ev.end_time); }
    else colEnd[col] = ev.end_time;
    assigned.push(col);
  }
  return sorted.map((event, i) => {
    let numCols = 1;
    for (let j = 0; j < sorted.length; j++) {
      if (event.slot < sorted[j].end_time && event.end_time > sorted[j].slot) {
        numCols = Math.max(numCols, assigned[j] + 1);
      }
    }
    return { event, leftFrac: assigned[i] / numCols, widthFrac: 1 / numCols };
  });
}

// Only show events whose date matches the column exactly.
// All events are expected to carry an explicit date field.
function eventsForColumn(events: ScheduleEvent[], dow: number, colDateStr: string): ScheduleEvent[] {
  return events.filter((e) => e.day === dow && e.date === colDateStr);
}

// ── Date helpers ──────────────────────────────────────────────────────────
const DAY_SHORT = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
const MONTH_NAMES = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
const WEEK_LABELS = ["一", "二", "三", "四", "五", "六", "日"];

function getMonday(d: Date): Date {
  const day = d.getDay();
  const m = new Date(d);
  m.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  m.setHours(0, 0, 0, 0);
  return m;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function jsDayToDow(jsDay: number): number { return (jsDay + 6) % 7; }
function fmtDateCN(d: Date): string { return `${d.getMonth() + 1}月${d.getDate()}日`; }
function firstDowOfMonth(d: Date): number { return jsDayToDow(new Date(d.getFullYear(), d.getMonth(), 1).getDay()); }
function daysInMonth(d: Date): number { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

// ── Public API ────────────────────────────────────────────────────────────
export interface ScheduleProps {
  range: "day" | "week" | "month";
  events: ScheduleEvent[];
  students?: Student[];
  onAddSlot?: (dayOfWeek: number, time: string, date: Date) => void;
  onEditEvent?: (event: ScheduleEvent) => void;
}

// ── Shared styles ─────────────────────────────────────────────────────────
const navBtnStyle: CSSProperties = {
  border: "none",
  background: "var(--surface-sunken)",
  borderRadius: "var(--radius-sm)",
  padding: "5px 9px",
  cursor: "pointer",
  color: "var(--text-muted)",
  display: "flex",
  alignItems: "center",
  lineHeight: 1,
};

const todayBtnStyle: CSSProperties = {
  ...navBtnStyle,
  fontSize: 12,
  fontWeight: 600,
  padding: "5px 12px",
  color: "var(--brand)",
  background: "var(--brand-subtle)",
};

// Grid lines drawn as a repeating gradient on backgroundImage
const GRID_BG = `repeating-linear-gradient(to bottom, transparent, transparent calc(${PX_PER_HOUR}px - 1px), var(--divider) calc(${PX_PER_HOUR}px - 1px), var(--divider) ${PX_PER_HOUR}px)`;

// ── EventBlock — absolutely positioned inside the time-grid column ─────────
interface EventBlockProps {
  layout: EventLayout;
  students?: Student[];
  onEdit?: (ev: ScheduleEvent) => void;
}

function EventBlock({ layout: { event, leftFrac, widthFrac }, students, onEdit }: EventBlockProps) {
  const top = timeToY(event.slot) + 1;
  const height = Math.max(timeToHeight(event.slot, event.end_time) - 2, 22);
  const compact = height < 46;

  const isGroup = (event.studentIds?.length ?? 0) > 1;

  // Resolve member names for group classes; fall back to event.name for 1:1
  const memberNames: string[] = isGroup && students
    ? event.studentIds!.map(id => students.find(s => s.id === id)?.name ?? "").filter(Boolean)
    : [];

  // Group classes use a slightly deeper tint to distinguish from 一对一
  const fgColor = event.status === "taught" ? "var(--status-taught-fg)"
    : event.status === "leave" ? "var(--status-leave-fg)"
    : event.status === "absent" ? "var(--status-absent-fg)"
    : "var(--brand)";

  const bgColor = event.status === "taught" ? "var(--status-taught-bg)"
    : event.status === "leave" ? "var(--status-leave-bg)"
    : event.status === "absent" ? "var(--status-absent-bg)"
    : isGroup
      ? "color-mix(in srgb, var(--brand) 22%, transparent)"   // group: noticeably darker
      : "var(--brand-subtle)";                                  // 1:1: lighter tint

  // Attendance dots for group classes — one per participant
  const statDots = isGroup && event.studentIds && event.studentStatuses
    ? event.studentIds.map((id) => {
        const st = event.studentStatuses![id];
        if (!st || st === "upcoming") return null;
        const dotColor = st === "taught" ? "var(--status-taught-fg)"
          : st === "leave" ? "var(--status-leave-fg)"
          : "var(--status-absent-fg)";
        const dotLabel = st === "absent" ? "缺" : st === "leave" ? "假" : "✓";
        return { id, color: dotColor, label: dotLabel };
      }).filter(Boolean)
    : [];

  return (
    <div
      data-event="1"
      onClick={(e) => { e.stopPropagation(); onEdit?.(event); }}
      style={{
        position: "absolute",
        top,
        height,
        left: `calc(${leftFrac * 100}% + 2px)`,
        width: `calc(${widthFrac * 100}% - 4px)`,
        zIndex: 1,
        cursor: "pointer",
        backgroundColor: bgColor,
        borderLeft: `3px solid ${fgColor}`,
        borderRadius: "var(--radius-sm)",
        padding: compact ? "2px 5px" : "4px 7px",
        overflow: "hidden",
        boxSizing: "border-box",
        userSelect: "none",
      }}
    >
      {/* Name row — group: comma-separated on one line */}
      <div style={{ fontSize: 11, fontWeight: 700, color: fgColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.35 }}>
        {isGroup && memberNames.length > 0 ? memberNames.join("、") : event.name}
      </div>

      {!compact && (
        <>
          <div style={{ fontSize: 10, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}>
            {event.subject}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-subtle)", fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}>
            {event.slot}–{event.end_time}
          </div>
        </>
      )}

      {/* Per-student attendance badges (group classes only) */}
      {statDots.length > 0 && (
        <div style={{ display: "flex", gap: 3, marginTop: 2, flexWrap: "wrap" }}>
          {statDots.map((dot) => dot && (
            <span key={dot.id} style={{
              fontSize: 9,
              fontWeight: 800,
              padding: "1px 4px",
              borderRadius: 99,
              background: dot.color,
              color: "#fff",
              lineHeight: 1.4,
            }}>
              {dot.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TimeLabelsColumn — left ruler strip ──────────────────────────────────
function TimeLabelsColumn() {
  const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
  return (
    <div style={{
      position: "relative",
      width: 52,
      flexShrink: 0,
      height: TOTAL_H,
      borderRight: "1px solid var(--divider)",
      userSelect: "none",
    }}>
      {hours.map((h, i) => (
        <div key={h} style={{
          position: "absolute",
          // First label: start just below the top edge; subsequent: just above their grid line
          top: i === 0 ? 4 : i * PX_PER_HOUR - 8,
          right: 8,
          fontSize: 10,
          fontWeight: 700,
          color: "var(--text-subtle)",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}>
          {String(h).padStart(2, "0")}:00
        </div>
      ))}
    </div>
  );
}

// ── DayColumn — one day's absolute-positioned event column ───────────────
interface DayColumnProps {
  dow: number;
  date: Date;
  dateStr: string;
  isToday: boolean;
  isWeekend: boolean;
  nowY: number | null;
  events: ScheduleEvent[];
  students?: Student[];
  showBorderRight: boolean;
  onAddSlot?: (dow: number, time: string, date: Date) => void;
  onEditEvent?: (event: ScheduleEvent) => void;
}

function DayColumn({ dow, date, dateStr, isToday, isWeekend, nowY, events, students, showBorderRight, onAddSlot, onEditEvent }: DayColumnProps) {
  const colEvents = eventsForColumn(events, dow, dateStr);
  const layouts = layoutDayEvents(colEvents);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("[data-event]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    onAddSlot?.(dow, yToTime(y), date);
  };

  return (
    <div
      onClick={handleClick}
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: TOTAL_H,
        cursor: "pointer",
        backgroundColor: isToday
          ? "color-mix(in srgb, var(--brand-subtle) 40%, transparent)"
          : isWeekend
          ? "color-mix(in srgb, var(--neutral-100) 40%, transparent)"
          : "transparent",
        backgroundImage: GRID_BG,
        borderRight: showBorderRight ? "1px solid var(--divider)" : "none",
      }}
    >
      {layouts.map((layout) => (
        <EventBlock key={layout.event.id} layout={layout} students={students} onEdit={onEditEvent} />
      ))}

      {/* Current time red accent line */}
      {isToday && nowY !== null && (
        <div style={{
          position: "absolute",
          top: nowY,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: "var(--danger-fg, #e53935)",
          zIndex: 3,
          pointerEvents: "none",
        }}>
          <div style={{
            position: "absolute",
            left: -4,
            top: -4,
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "var(--danger-fg, #e53935)",
          }} />
        </div>
      )}
    </div>
  );
}

// ── Main Schedule component ───────────────────────────────────────────────
export function Schedule({ range, events, students, onAddSlot, onEditEvent }: ScheduleProps) {
  const isMobile = useIsMobile();
  const [anchor, setAnchor] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [nowY, setNowY] = useState<number | null>(null);

  useLucideIcons([range, anchor.getTime(), events.length]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      setNowY(h >= START_HOUR && h < END_HOUR
        ? ((h - START_HOUR) + m / 60) * PX_PER_HOUR
        : null);
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, []);

  const mobileWeek = range === "week" && isMobile;

  const navigate = (delta: number) => {
    const d = new Date(anchor);
    if (range === "day") d.setDate(d.getDate() + delta);
    else if (range === "week") d.setDate(d.getDate() + delta * (mobileWeek ? 3 : 7));
    else d.setMonth(d.getMonth() + delta);
    setAnchor(d);
  };

  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setAnchor(d);
  };

  const NavBar = ({ label }: { label: string }) => (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderBottom: "1px solid var(--border-subtle)",
      background: "var(--surface-card)",
      flexShrink: 0,
    }}>
      <button onClick={() => navigate(-1)} style={navBtnStyle} title="上一期">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <button onClick={() => navigate(1)} style={navBtnStyle} title="下一期">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <span style={{ flex: 1, fontWeight: 700, fontSize: 14, color: "var(--text-strong)", paddingLeft: 6 }}>
        {label}
      </span>
      <button onClick={goToday} style={todayBtnStyle}>今天</button>
    </div>
  );

  // ── Week view ─────────────────────────────────────────────────────────
  if (range === "week") {
    // Mobile: show 3 days starting from anchor; Desktop: show full week Mon–Sun
    const startDay = mobileWeek ? anchor : getMonday(anchor);
    const numDays = mobileWeek ? 3 : 7;
    const weekDays = Array.from({ length: numDays }, (_, i) => {
      const d = addDays(startDay, i);
      const dow = (d.getDay() + 6) % 7;
      return {
        date: d,
        dateStr: dateToDowStr(d),
        label: DAY_SHORT[dow],
        shortDate: `${d.getMonth() + 1}/${d.getDate()}`,
        isToday: sameDay(d, today),
        isWeekend: dow >= 5,
        dow,
      };
    });
    const endDate = addDays(startDay, numDays - 1);
    const weekLabel = startDay.getMonth() === endDate.getMonth()
      ? `${startDay.getMonth() + 1}月${startDay.getDate()}日 – ${endDate.getDate()}日`
      : `${startDay.getMonth() + 1}月${startDay.getDate()}日 – ${endDate.getMonth() + 1}月${endDate.getDate()}日`;

    return (
      <Card pad={false} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <NavBar label={weekLabel} />

        {/* Scrollable area — headers go INSIDE so scrollbar doesn't offset column widths */}
        <div style={{ overflow: "auto", flex: 1, maxHeight: "calc(100vh - 160px)" }}>

          {/* Sticky day headers — inside the scroll container */}
          <div style={{
            display: "flex",
            borderBottom: "1px solid var(--border-subtle)",
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: "var(--surface-card)",
          }}>
            {/* Time column spacer */}
            <div style={{ width: 52, flexShrink: 0, borderRight: "1px solid var(--divider)" }} />
            {weekDays.map((d) => (
              <div
                key={d.dow}
                style={{
                  flex: 1,
                  padding: "8px 4px 10px",
                  textAlign: "center",
                  background: d.isToday ? "var(--brand-subtle)" : d.isWeekend ? "var(--surface-sunken)" : "transparent",
                  borderRight: d.dow < 6 ? "1px solid var(--divider)" : "none",
                }}
              >
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: d.isToday ? "var(--brand)" : d.isWeekend ? "var(--accent)" : "var(--text-subtle)",
                  marginBottom: 3,
                }}>
                  {d.label}
                </div>
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: d.isToday ? 26 : "auto",
                  height: d.isToday ? 26 : "auto",
                  borderRadius: "50%",
                  background: d.isToday ? "var(--brand)" : "transparent",
                  color: d.isToday ? "#fff" : d.isWeekend ? "var(--accent)" : "var(--text-body)",
                  fontFamily: "var(--font-num)",
                  fontWeight: d.isToday ? 800 : 500,
                  fontSize: 13,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {d.shortDate}
                </div>
              </div>
            ))}
          </div>

          {/* Time grid */}
          <div style={{ display: "flex", height: TOTAL_H }}>
            <TimeLabelsColumn />
            {weekDays.map((day, idx) => (
              <DayColumn
                key={day.dateStr}
                dow={day.dow}
                date={day.date}
                dateStr={day.dateStr}
                isToday={day.isToday}
                isWeekend={day.isWeekend}
                nowY={day.isToday ? nowY : null}
                events={events}
                students={students}
                showBorderRight={idx < weekDays.length - 1}
                onAddSlot={onAddSlot}
                onEditEvent={onEditEvent}
              />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  // ── Day view ──────────────────────────────────────────────────────────
  if (range === "day") {
    const dow = jsDayToDow(anchor.getDay());
    const isAnchorToday = sameDay(anchor, today);
    const dayLabel = `${fmtDateCN(anchor)}  ${DAY_SHORT[dow]}`;
    const anchorStr = dateToDowStr(anchor);

    return (
      <Card pad={false} style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <NavBar label={dayLabel} />
        <div style={{ overflow: "auto", flex: 1, maxHeight: "calc(100vh - 160px)" }}>
          <div style={{ display: "flex", height: TOTAL_H }}>
            <TimeLabelsColumn />
            <DayColumn
              dow={dow}
              date={anchor}
              dateStr={anchorStr}
              isToday={isAnchorToday}
              isWeekend={dow >= 5}
              nowY={isAnchorToday ? nowY : null}
              events={events}
              students={students}
              showBorderRight={false}
              onAddSlot={onAddSlot}
              onEditEvent={onEditEvent}
            />
          </div>
        </div>
      </Card>
    );
  }

  // ── Month view ────────────────────────────────────────────────────────
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const firstDow = firstDowOfMonth(anchor);
  const numDays = daysInMonth(anchor);
  const monthLabel = `${year}年${MONTH_NAMES[month]}`;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= numDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Card pad={false} style={{ overflow: "hidden" }}>
      <NavBar label={monthLabel} />
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        borderBottom: "1px solid var(--border-subtle)",
        background: "var(--surface-sunken)",
      }}>
        {WEEK_LABELS.map((label, i) => (
          <div key={label} style={{
            padding: "7px 0",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 700,
            color: i >= 5 ? "var(--accent)" : "var(--text-subtle)",
          }}>
            {label}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((day, i) => {
          const col = i % 7;
          const borderR = col < 6 ? "1px solid var(--divider)" : "none";
          const borderB = "1px solid var(--divider)";

          if (!day) {
            return (
              <div key={`e${i}`} style={{
                minHeight: 88,
                borderBottom: borderB,
                borderRight: borderR,
                background: "var(--surface-sunken)",
                opacity: 0.5,
              }} />
            );
          }

          const cellDate = new Date(year, month, day);
          const dow = jsDayToDow(cellDate.getDay());
          const cellDateStr = dateToDowStr(cellDate);
          const isToday = sameDay(cellDate, today);
          const isWeekend = col >= 5;
          const dayEvents = eventsForColumn(events, dow, cellDateStr);

          return (
            <div
              key={day}
              onClick={() => onAddSlot?.(dow, "16:00", cellDate)}
              style={{
                minHeight: 88,
                padding: "6px 6px 8px",
                borderBottom: borderB,
                borderRight: borderR,
                cursor: "pointer",
                background: isToday
                  ? "var(--brand-subtle)"
                  : isWeekend
                  ? "color-mix(in srgb, var(--neutral-100) 40%, transparent)"
                  : "transparent",
                overflow: "hidden",
              }}
            >
              <div style={{ marginBottom: 4 }}>
                {isToday ? (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "var(--brand)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 800,
                  }}>
                    {day}
                  </span>
                ) : (
                  <span style={{
                    fontSize: 13,
                    fontWeight: isWeekend ? 700 : 400,
                    color: isWeekend ? "var(--accent)" : "var(--text-body)",
                  }}>
                    {day}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                {dayEvents.slice(0, 3).map((ev) => (
                  <div
                    key={ev.id}
                    onClick={(e) => { e.stopPropagation(); onEditEvent?.(ev); }}
                    style={{
                      fontSize: 10,
                      borderRadius: 3,
                      padding: "2px 5px",
                      background: ev.status === "taught" ? "var(--status-taught-bg)"
                        : ev.status === "leave" ? "var(--status-leave-bg)"
                        : ev.status === "absent" ? "var(--status-absent-bg)"
                        : "var(--brand-subtle)",
                      color: ev.status === "taught" ? "var(--status-taught-fg)"
                        : ev.status === "leave" ? "var(--status-leave-fg)"
                        : ev.status === "absent" ? "var(--status-absent-fg)"
                        : "var(--brand)",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    {ev.slot} {ev.name}
                    {(ev.studentIds?.length ?? 0) > 1 && ` ·${ev.studentIds!.length}人`}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: 10, color: "var(--text-subtle)", paddingLeft: 4 }}>
                    +{dayEvents.length - 3} 节课
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
